import TextField, { TextFieldProps } from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback } from 'react';
import React from 'react';

import Button from '@/components/Button';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import { OTP_METHODS, OtpMethod } from '@/core/api';
import { darkGreen } from '@/theme/colors';

const useStyles = makeStyles({
  root: {
    textAlign: 'center',
  },
  code: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    marginTop: 25,
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 25,
  },
  submitButton: {
    margin: '20px 0 0 80px',
  },
  cancelButton: {
    margin: '20px 80px 0 0',
  },
  resendButton: {
    marginTop: 25,
    padding: '8px 11px',
  },
  sms: {
    marginTop: 10,
  },
  smsButton: {
    fontSize: 14,
    color: darkGreen,
    textTransform: 'none',
    fontWeight: 400,
  },
  title: {
    fontSize: 36,
  },
  loginErrorLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
    color: darkGreen,
    textDecoration: 'underline',
  },
});

const securityEmail = 'hello@onfrontiers.com';

export interface VerificationCodeProps {
  onSubmit: () => void | Promise<void>;
  onChange: TextFieldProps['onChange'];
  onCancel: () => void;
  onResend: () => void;
  code: string;
  error: string;
  method: OtpMethod;
  maskedAddress: string;
  buttonResendDisable: boolean;
  disabled?: boolean;
}

const VerificationCode = ({
  onSubmit,
  onChange,
  onCancel,
  onResend,
  code,
  error,
  method,
  maskedAddress,
  buttonResendDisable,
  disabled = false,
}: VerificationCodeProps): JSX.Element => {
  const s = useStyles();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') onSubmit();
    },
    [onSubmit]
  );

  return (
    <div className={s.root}>
      <h1 className={s.title}>Enter the {method} code</h1>
      {method !== OTP_METHODS.APP && (
        <p>
          {' '}
          The {method} code sent to {maskedAddress}{' '}
        </p>
      )}

      <div className={s.code}>
        <TextField
          label="Verification Code"
          variant="outlined"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={code}
          onChange={onChange}
          error={!!error}
          helperText={error}
          fullWidth={false}
          onKeyDown={handleKeyDown}
        />
        {method !== OTP_METHODS.APP && (
          <Button
            variant="text"
            classes={{ root: s.resendButton }}
            onClick={onResend}
            disabled={buttonResendDisable}
            size="medium"
          >
            Resend
          </Button>
        )}
      </div>
      {method === OTP_METHODS.EMAIL && <EmailDeliveryHelp align="center" className="mt-16" />}
      <div className={s.button}>
        <Button
          classes={{ root: s.cancelButton }}
          onClick={onCancel}
          fullWidth={false}
          variant="text"
          size="medium"
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          classes={{ root: s.submitButton }}
          onClick={onSubmit}
          fullWidth={false}
          size="medium"
        >
          Submit
        </Button>
      </div>
      <div className={s.sms}>
        <span>
          If you have issues please contact&nbsp;
          <a className={s.loginErrorLink} href={`mailto:${securityEmail}`}>
            {securityEmail}
          </a>
        </span>
      </div>
    </div>
  );
};

export default VerificationCode;
